import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Scale,
  Plus,
  Search,
  Filter,
  Edit,
  Eye,
  Power,
  Trash2,
  TrendingUp,
  Clock,
  Target,
  CheckCircle2,
  AlertCircle,
  Copy,
  MoreHorizontal,
  Activity,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import type { DisputeType, DisputeTypeFilters } from '@/types/dispute-types';
import { DisputeTypeEditor } from '@/components/settings/dispute-type-editor';
import { WorkflowViewer } from '@/components/settings/workflow-viewer';
import {
  useDisputeTypesList,
  useCreateDisputeType,
  useDeleteDisputeType,
  useToggleDisputeTypeStatus,
} from '@/hooks/useDisputeTypesData';

const DisputeTypesPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: disputeTypes = [], isLoading, isError, error } = useDisputeTypesList();
  const createDisputeTypeMutation = useCreateDisputeType();
  const deleteDisputeTypeMutation = useDeleteDisputeType();
  const toggleStatusMutation = useToggleDisputeTypeStatus();

  const [filters, setFilters] = useState<DisputeTypeFilters>({
    search: '',
    status: 'all',
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const [selectedDisputeType, setSelectedDisputeType] = useState<DisputeType | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [disputeTypeToDelete, setDisputeTypeToDelete] = useState<DisputeType | null>(null);

  // Calculate statistics
  const stats = useMemo(() => {
    const activeTypes = disputeTypes.filter(dt => dt.isActive);
    const totalDisputes = disputeTypes.reduce((sum, dt) => sum + dt.usageCount, 0);
    const avgResolutionTime = disputeTypes.length > 0
      ? Math.round(disputeTypes.reduce((sum, dt) => sum + dt.avgResolutionTime, 0) / disputeTypes.length)
      : 0;
    const mostUsed = disputeTypes.reduce((max, dt) => 
      dt.usageCount > max.usageCount ? dt : max
    , disputeTypes[0] || { name: 'N/A', usageCount: 0 });

    return {
      totalTypes: disputeTypes.length,
      activeTypes: activeTypes.length,
      totalDisputes,
      avgResolutionTime,
      mostUsedType: mostUsed.name,
    };
  }, [disputeTypes]);

  // Filter and sort dispute types
  const filteredDisputeTypes = useMemo(() => {
    let filtered = [...disputeTypes];

    // Apply status filter
    if (filters.status !== 'all') {
      filtered = filtered.filter(dt => 
        filters.status === 'active' ? dt.isActive : !dt.isActive
      );
    }

    // Apply search filter
    if (filters.search) {
      const query = filters.search.toLowerCase();
      filtered = filtered.filter(dt =>
        dt.name.toLowerCase().includes(query) ||
        dt.description.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (filters.sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'usage':
          comparison = a.usageCount - b.usageCount;
          break;
        case 'created':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'updated':
          comparison = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }

      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [disputeTypes, filters]);

  const handleCreateNew = () => {
    setSelectedDisputeType(null);
    setIsEditorOpen(true);
  };

  const handleEdit = (disputeType: DisputeType) => {
    navigate(`/settings/dispute-types/${disputeType.id}/workflow`);
  };

  const handleView = (disputeType: DisputeType) => {
    navigate(`/settings/dispute-types/${disputeType.id}/workflow`);
  };

  const handleDuplicate = (disputeType: DisputeType) => {
    createDisputeTypeMutation.mutate(
      {
        name: `${disputeType.name} (Copy)`,
        description: disputeType.description,
        isActive: disputeType.isActive,
      },
      {
        onSuccess: () => {
          toast.success('Dispute type duplicated successfully');
        },
        onError: (err) => {
          toast.error(`Failed to duplicate dispute type: ${err.message}`);
        },
      }
    );
  };

  const handleToggleStatus = (disputeType: DisputeType) => {
    toggleStatusMutation.mutate(
      { id: disputeType.id, isActive: !disputeType.isActive },
      {
        onSuccess: () => {
          toast.success(`Dispute type ${disputeType.isActive ? 'deactivated' : 'activated'} successfully`);
        },
        onError: (err) => {
          toast.error(`Failed to update dispute type status: ${err.message}`);
        },
      }
    );
  };

  const handleDeleteClick = (disputeType: DisputeType) => {
    setDisputeTypeToDelete(disputeType);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (disputeTypeToDelete) {
      if (disputeTypeToDelete.usageCount > 0) {
        toast.error('Cannot delete dispute type with active disputes. Please deactivate it instead.');
        setIsDeleteDialogOpen(false);
        setDisputeTypeToDelete(null);
      } else {
        deleteDisputeTypeMutation.mutate(disputeTypeToDelete.id, {
          onSuccess: () => {
            toast.success('Dispute type deleted successfully');
            setIsDeleteDialogOpen(false);
            setDisputeTypeToDelete(null);
          },
          onError: (err) => {
            toast.error(`Failed to delete dispute type: ${err.message}`);
            setIsDeleteDialogOpen(false);
            setDisputeTypeToDelete(null);
          },
        });
      }
    }
  };

  const handleSave = (disputeType: DisputeType) => {
    if (selectedDisputeType) {
      toast.success('Dispute type updated successfully');
      setIsEditorOpen(false);
    } else {
      createDisputeTypeMutation.mutate(
        {
          name: disputeType.name,
          description: disputeType.description,
          isActive: disputeType.isActive,
        },
        {
          onSuccess: () => {
            toast.success('Dispute type created successfully');
            setIsEditorOpen(false);
          },
          onError: (err) => {
            toast.error(`Failed to create dispute type: ${err.message}`);
          },
        }
      );
    }
  };

  const getPerformanceBadge = (successRate: number) => {
    if (successRate >= 90) return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Excellent</Badge>;
    if (successRate >= 75) return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">Good</Badge>;
    if (successRate >= 60) return <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">Average</Badge>;
    return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">Needs Improvement</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading dispute types...</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h2 className="text-lg font-semibold">Failed to load dispute types</h2>
        <p className="text-muted-foreground mt-1">
          {error?.message || 'An unexpected error occurred'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Scale className="h-8 w-8" />
            Dispute Types
          </h1>
          <p className="text-muted-foreground mt-1">
            Configure dispute types and customise workflow stages for each type
          </p>
        </div>
        <Button onClick={handleCreateNew}>
          <Plus className="h-4 w-4 mr-2" />
          Create Dispute Type
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Types</CardTitle>
            <Scale className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalTypes}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.activeTypes} active
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Disputes</CardTitle>
            <Activity className="h-4 w-4 text-emerald-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalDisputes}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Across all types
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Resolution Time</CardTitle>
            <Clock className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.avgResolutionTime} days</div>
            <p className="text-xs text-muted-foreground mt-1">
              Average across all types
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Most Used Type</CardTitle>
            <Target className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold truncate">{stats.mostUsedType}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Highest usage count
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search dispute types..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="pl-9"
          />
        </div>
        <Select
          value={filters.status}
          onValueChange={(value: 'all' | 'active' | 'inactive') => setFilters({ ...filters, status: value })}
        >
          <SelectTrigger className="w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.sortBy}
          onValueChange={(value: 'name' | 'usage' | 'created' | 'updated') => setFilters({ ...filters, sortBy: value })}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">Name</SelectItem>
            <SelectItem value="usage">Usage Count</SelectItem>
            <SelectItem value="created">Created Date</SelectItem>
            <SelectItem value="updated">Updated Date</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setFilters({ ...filters, sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' })}
        >
          <TrendingUp className={`h-4 w-4 transition-transform ${filters.sortOrder === 'desc' ? 'rotate-180' : ''}`} />
        </Button>
      </div>

      {/* Dispute Types Table */}
      <Card>
        <CardHeader>
          <CardTitle>Dispute Types ({filteredDisputeTypes.length})</CardTitle>
          <CardDescription>
            Manage dispute type configurations and workflows
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name & Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Workflow Stages</TableHead>
                <TableHead>Usage Count</TableHead>
                <TableHead>Avg Resolution</TableHead>
                <TableHead>Success Rate</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDisputeTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                    No dispute types found matching your criteria
                  </TableCell>
                </TableRow>
              ) : (
                filteredDisputeTypes.map((disputeType) => (
                  <TableRow key={disputeType.id}>
                    <TableCell>
                      <div>
                        <button
                          onClick={() => handleView(disputeType)}
                          className="font-medium text-primary hover:underline text-left"
                        >
                          {disputeType.name}
                        </button>
                        <div className="text-sm text-muted-foreground max-w-md truncate">
                          {disputeType.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={disputeType.isActive}
                          onCheckedChange={() => handleToggleStatus(disputeType)}
                        />
                        <span className="text-sm">
                          {disputeType.isActive ? (
                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Inactive</Badge>
                          )}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                        {disputeType.workflow.stages.length} stages
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-muted-foreground" />
                        {disputeType.usageCount}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        {disputeType.avgResolutionTime} days
                      </div>
                    </TableCell>
                    <TableCell>
                      {disputeType.usageCount > 0 ? (
                        <div className="flex items-center gap-2">
                          {getPerformanceBadge(disputeType.successRate)}
                          <span className="text-sm text-muted-foreground">
                            {disputeType.successRate}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">No data</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {format(new Date(disputeType.updatedAt), 'MMM dd, yyyy')}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleView(disputeType)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Workflow
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEdit(disputeType)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Configuration
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDuplicate(disputeType)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Duplicate
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleToggleStatus(disputeType)}>
                            <Power className="h-4 w-4 mr-2" />
                            {disputeType.isActive ? 'Deactivate' : 'Activate'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleDeleteClick(disputeType)}
                            className="text-destructive"
                            disabled={disputeType.usageCount > 0}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dispute Type Editor Dialog */}
      {isEditorOpen && (
        <DisputeTypeEditor
          disputeType={selectedDisputeType}
          open={isEditorOpen}
          onClose={() => setIsEditorOpen(false)}
          onSave={handleSave}
        />
      )}

      {/* Workflow Viewer Dialog */}
      {isViewerOpen && selectedDisputeType && (
        <WorkflowViewer
          disputeType={selectedDisputeType}
          open={isViewerOpen}
          onClose={() => setIsViewerOpen(false)}
          onEdit={() => {
            setIsViewerOpen(false);
            setIsEditorOpen(true);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Dispute Type</AlertDialogTitle>
            <AlertDialogDescription>
              {disputeTypeToDelete?.usageCount && disputeTypeToDelete.usageCount > 0 ? (
                <>
                  <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
                    <AlertCircle className="h-4 w-4" />
                    <span className="font-medium">Cannot delete this dispute type</span>
                  </div>
                  <p>
                    This dispute type has {disputeTypeToDelete.usageCount} active dispute(s) associated with it.
                    Please deactivate it instead or reassign the disputes to another type.
                  </p>
                </>
              ) : (
                <>
                  Are you sure you want to delete "{disputeTypeToDelete?.name}"? This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {(!disputeTypeToDelete?.usageCount || disputeTypeToDelete.usageCount === 0) && (
              <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DisputeTypesPage;
